/*
 * HomeFixer360 - Early Access Page
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Zap, Users, Gift, Star, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LeadCaptureModal from '../components/ui/LeadCaptureModal';
import Navigation from '../components/landing/Navigation';

const EarlyAccess = () => {
  const [showPresaleModal, setShowPresaleModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    applyForBetaTester: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Show presale modal after 15 seconds (delayed to avoid blocking form)
  // Only show if user hasn't started filling the form
  useEffect(() => {
    const timer = setTimeout(() => {
      // Don't show modal if user has already started filling the form
      if (!formData.firstName && !formData.lastName && !formData.email) {
        setShowPresaleModal(true);
      }
    }, 15000); // Increased from 4 to 15 seconds

    return () => clearTimeout(timer);
  }, [formData.firstName, formData.lastName, formData.email]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          source: 'early_access_page',
          interests: formData.applyForBetaTester ? ['early_access', 'beta_tester'] : ['early_access'],
          notes: formData.applyForBetaTester 
            ? 'Early access signup - APPLIED FOR BETA TESTER (1 of 50 spots)' 
            : 'Early access signup from dedicated page'
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          window.location.href = '/';
        }, 5000);
      } else {
        throw new Error('Failed to submit early access form');
      }
    } catch (error) {
      console.error('Error submitting early access form:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePresaleModal = () => {
    setShowPresaleModal(false);
    // Stay on early access page
  };

  const isFormValid = formData.firstName && formData.lastName && formData.email && formData.phone;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">🎉 Welcome to Early Access! 🎉</h2>
          <p className="text-gray-600 mb-6">
            Thank you <strong>{formData.firstName}</strong>! You're now on the early access list for HomeFixer360.
          </p>
          
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4 mb-6 border border-blue-200">
            <h3 className="font-bold text-blue-800 mb-2">What's Next:</h3>
            <div className="text-sm text-blue-700 space-y-1">
              <div>✅ Priority beta access notifications</div>
              <div>✅ Exclusive presale pricing when available</div>
              <div>✅ Product updates and launch news</div>
            </div>
          </div>
          
          <p className="text-sm text-gray-500">
            Returning to homepage in a few seconds...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
      {/* Navigation */}
      <Navigation />
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Get Started</h1>
            <p className="text-gray-600">Reserve your spot in our exclusive program</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Be First to Experience the Future of Roof Measurement
          </h2>
          <p className="text-xl text-gray-600 mb-6 max-w-2xl mx-auto">
            Join our exclusive early access program and get priority access to HomeFixer360, 
            plus special launch pricing and beta testing opportunities.
          </p>
          
          <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white rounded-2xl p-6 max-w-2xl mx-auto mb-8">
            <div className="font-bold text-lg mb-2">📅 Launch Timeline</div>
            <div className="text-blue-100">
              <div className="mb-1"><strong>Beta Period:</strong> Now – February 28, 2026</div>
              <div className="mb-1"><strong>Presale Ends:</strong> February 28, 2026</div>
              <div><strong>Official Launch:</strong> March 1, 2026</div>
            </div>
            <div className="mt-3 bg-orange-500 rounded-lg px-4 py-2 text-white font-semibold text-center">
              Only 50 Beta Testers Available!
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Priority Access</h3>
            <p className="text-gray-600">
              Be among the first to access HomeFixer360 when we launch. Skip the waiting list.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Exclusive Pricing</h3>
            <p className="text-gray-600">
              Get special early access pricing including 50% off setup fees and discounted monthly rates.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Beta Testing</h3>
            <p className="text-gray-600">
              Help shape the product by participating in our private beta program before public launch.
            </p>
          </div>
        </div>

        {/* Early Access Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Reserve Your Early Access Spot</h3>
            <p className="text-gray-600 mb-4">Fill out the form below to join our exclusive early access program</p>
            <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 text-sm">
              <div className="font-semibold text-orange-800">⏰ Limited Time Offer</div>
              <div className="text-orange-700">Beta: Now – Feb. 28 • Launch: Mar 1, 2026 • Presale Ends: Feb 28</div>
              <div className="text-orange-800 font-bold mt-1">Only 50 Beta Tester Spots Available!</div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Smith"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="john@company.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  required
                />
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="company">Company Name</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder="Your Home Service Company"
              />
            </div>

            {/* Beta Tester Application Checkbox */}
            <div className="mb-8 bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="applyForBetaTester"
                  checked={formData.applyForBetaTester}
                  onChange={(e) => handleInputChange('applyForBetaTester', e.target.checked)}
                  className="mt-1 w-5 h-5 text-orange-600 border-orange-300 rounded focus:ring-orange-500"
                />
                <div className="flex-1">
                  <label htmlFor="applyForBetaTester" className="font-semibold text-gray-900 cursor-pointer">
                    🔥 I want to apply for a Beta Tester spot (Only 50 available!)
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    Get exclusive access to HomeFixer360 from now until February 28, 2026. 
                    Beta testers receive special pricing and direct input on features.
                  </p>
                </div>
              </div>
              {formData.applyForBetaTester && (
                <div className="mt-3 bg-green-100 border border-green-300 rounded-lg p-3 text-sm">
                  <div className="flex items-center text-green-800 font-semibold">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    You're applying for Beta Tester access!
                  </div>
                  <p className="text-green-700 text-xs mt-1">
                    We'll prioritize your application and contact you with next steps.
                  </p>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="w-full bg-gradient-to-r from-blue-600 to-orange-500 text-white py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Securing Your Spot...</span>
                </div>
              ) : (
                <>
                  <Zap className="w-5 h-5 inline mr-2" />
                  Reserve My Early Access Spot
                </>
              )}
            </Button>

            <p className="text-xs text-gray-600 text-center mt-4">
              We respect your privacy. Unsubscribe at any time. No spam, just launch updates.
            </p>
          </form>
        </div>

        {/* Additional Benefits */}
        <div className="text-center mt-12">
          <div className="bg-gradient-to-r from-blue-600 to-orange-500 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Why Get Started?</h3>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div className="text-left">
                <h4 className="font-semibold mb-2">🚀 Be First to Market</h4>
                <p className="opacity-90">Get competitive advantage by implementing satellite roof measurement technology before your competitors.</p>
              </div>
              <div className="text-left">
                <h4 className="font-semibold mb-2">💰 Special Launch Pricing</h4>
                <p className="opacity-90">Lock in exclusive pricing that won't be available after our public launch.</p>
              </div>
              <div className="text-left">
                <h4 className="font-semibold mb-2">🛠️ Shape the Product</h4>
                <p className="opacity-90">Your feedback during beta testing will directly influence the final product features.</p>
              </div>
              <div className="text-left">
                <h4 className="font-semibold mb-2">🎯 Priority Support</h4>
                <p className="opacity-90">Get direct access to our development team and priority customer support.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Presale Modal - appears after 4 seconds */}
      <LeadCaptureModal 
        isOpen={showPresaleModal} 
        onClose={handleClosePresaleModal} 
      />
    </div>
  );
};

export default EarlyAccess;